import { a3Copy } from "@/components/assignments/a3-copy";
/**
 * The choices a work's Session and Grade item fields offer: the course's sessions
 * by calendar, named as the material row names them, and its grade items with
 * their type and weight, because two lines may share a label.
 */
import { useQuery } from "@tanstack/react-query";

import { classLabel, classOptions } from "@/components/courses/materials/material-facts";
import { courseUnitsApi } from "@/lib/api/courseUnits";
import { gradebookApi } from "@/lib/api/gradebook";

import { NOT_GRADED, type WorkLabels } from "./work-facts";

export interface WorkChoices {
  options: Array<{ id: string; name: string }>;
  loading: boolean;
  failed: boolean;
}

export function useWorkOptions(courseId: string, timezone: string, enabled = true): WorkLabels & {
  classes: WorkChoices;
  gradeItems: WorkChoices;
} {
  const units = useQuery({
    queryKey: ["course-units", courseId],
    queryFn: () => courseUnitsApi.list(courseId),
    enabled,
  });
  // The canonical key: the Gradebook invalidates it when a line is added or removed.
  const items = useQuery({
    queryKey: ["gradebook", "items", courseId],
    queryFn: () => gradebookApi.getGradeItems(courseId),
    enabled,
  });

  return {
    classes: {
      options: classOptions(units.data ?? [], timezone),
      loading: units.isPending,
      failed: units.isError,
    },
    gradeItems: {
      options: (items.data ?? []).map((item) => ({
        id: item.id,
        name: a3Copy.weight(item.label, item.type, item.weight),
      })),
      loading: items.isPending,
      failed: items.isError,
    },
    classLabel: (unitId) => classLabel(unitId, units.data ?? [], timezone) || a3Copy.thisSession,
    gradeItemLabel: (gradeItemId) => {
      if (!gradeItemId) return NOT_GRADED;
      return items.data?.find((item) => item.id === gradeItemId)?.label ?? a3Copy.thisGradeItem;
    },
  };
}
